import { useState, type JSX } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import type { Notice } from '@dxtech/shared';
import { Modal, ConfirmDialog, Spinner, Avatar } from '../ui/common.js';
import { RichTextView } from '../ui/RichText.js';
import { api } from '../api/client.js';

/** ISO → KST 'YYYY.MM.DD HH:mm' */
function kstDateTime(iso?: string): string {
  if (!iso) return '';
  const s = new Intl.DateTimeFormat('sv-SE', {
    timeZone: 'Asia/Seoul', year: 'numeric', month: '2-digit', day: '2-digit',
    hour: '2-digit', minute: '2-digit', hour12: false,
  }).format(new Date(iso));
  return s.replace(/-/g, '.').replace(' ', ' ');
}
function dot(d?: string): string { return d ? d.replace(/-/g, '.') : ''; }

/**
 * 공지 상세 — 레이어드 팝업. 열람 시 서버가 열람 기록(markRead).
 * 열람자 목록은 Admin 에게 표시, 그 외에는 열람 인원 수만 표시.
 */
export function NoticeDetailModal(
  { id, isAdmin, onClose, onEdit, onDone }:
  { id: string; isAdmin: boolean; onClose: () => void; onEdit: (n: Notice) => void; onDone: (msg: string) => void },
): JSX.Element {
  const qc = useQueryClient();
  const [confirmDel, setConfirmDel] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const detail = useQuery({ queryKey: ['notice', id], queryFn: () => api.noticeDetail(id) });
  const n = detail.data?.notice;

  const doDelete = async (): Promise<void> => {
    setDeleting(true);
    try {
      await api.deleteNotice(id);
      await qc.invalidateQueries({ queryKey: ['notices'] });
      onDone('공지를 삭제했습니다');
      onClose();
    } catch (e) {
      onDone(e instanceof Error ? e.message : '삭제 실패');
      setDeleting(false);
      setConfirmDel(false);
    }
  };

  return (
    <Modal width={640} onClose={onClose}>
      <div style={{ maxHeight: '84vh', overflowY: 'auto' }}>
        {detail.isLoading || !n ? (
          <div style={{ padding: 60, textAlign: 'center' }}><Spinner /></div>
        ) : (
          <div style={{ padding: 24 }}>
            <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12, marginBottom: 6 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                {n.pinned && <span style={{ fontSize: 11, fontWeight: 700, color: '#B45309', background: '#FEF3C7', borderRadius: 6, padding: '2px 7px' }}>고정</span>}
                <div style={{ fontSize: 18, fontWeight: 700 }}>{n.title}</div>
              </div>
              <button title="닫기" onClick={onClose} style={{ border: 'none', background: 'transparent', cursor: 'pointer', color: '#6B7280', fontSize: 26, lineHeight: 1 }}>×</button>
            </div>
            <div style={{ fontSize: 12.5, color: '#9CA3AF', marginBottom: 16, display: 'flex', gap: 10, flexWrap: 'wrap' }}>
              <span>게시일 {dot(n.noticeDate)}</span><span>·</span><span>{n.authorName}</span>
              {n.updatedAt && <><span>·</span><span>수정됨</span></>}
            </div>

            <div style={{ borderTop: '1px solid #F3F4F6', paddingTop: 16, marginBottom: 18, minHeight: 60 }}>
              <RichTextView html={n.bodyHtml} />
            </div>

            {/* 열람 기록 */}
            <div style={{ borderTop: '1px solid #F3F4F6', paddingTop: 14 }}>
              <div style={{ fontSize: 12.5, fontWeight: 700, color: '#6B7280', marginBottom: 10 }}>
                열람 {n.reads.length}명
              </div>
              {isAdmin ? (
                n.reads.length === 0 ? (
                  <div style={{ fontSize: 12.5, color: '#9CA3AF' }}>아직 열람한 직원이 없습니다.</div>
                ) : (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 6, maxHeight: 180, overflowY: 'auto' }}>
                    {n.reads.map((r, i) => (
                      <div key={r.upn} style={{ display: 'flex', alignItems: 'center', gap: 9, fontSize: 12.5 }}>
                        <Avatar name={r.name} index={i} size={24} upn={r.upn} />
                        <span style={{ fontWeight: 600, color: '#374151' }}>{r.name}</span>
                        <span className="tnum" style={{ marginLeft: 'auto', color: '#9CA3AF' }}>{kstDateTime(r.readAt)}</span>
                      </div>
                    ))}
                  </div>
                )
              ) : (
                <div style={{ fontSize: 12, color: '#9CA3AF' }}>열람 명단은 관리자에게만 표시됩니다.</div>
              )}
            </div>

            {isAdmin && (
              <div style={{ display: 'flex', gap: 10, marginTop: 20, borderTop: '1px solid #F3F4F6', paddingTop: 16 }}>
                <button className="dx-btn dx-btn-danger-outline" onClick={() => setConfirmDel(true)}>삭제</button>
                <button className="dx-btn dx-btn-ghost" style={{ marginLeft: 'auto' }} onClick={onClose}>닫기</button>
                <button className="dx-btn dx-btn-primary" onClick={() => onEdit(n)}>수정</button>
              </div>
            )}
          </div>
        )}
      </div>

      {confirmDel && (
        <ConfirmDialog
          title="공지를 삭제할까요?"
          message="삭제하면 되돌릴 수 없으며 열람 기록도 함께 삭제됩니다."
          confirmLabel="삭제" danger busy={deleting}
          onConfirm={doDelete} onCancel={() => setConfirmDel(false)}
        />
      )}
    </Modal>
  );
}
